import api from './api';

/**
 * 债权/催告相关接口封装
 * 约定：
 * 1) 路径保持 RESTful；参数命名尽量与后端字段一致，便于直接透传。
 * 2) 统一通过 api 实例发起请求（见 ./api），由其处理 baseURL、鉴权、拦截器等。
 * 3) 本文件仅做“请求方法 + 路径 + 参数结构”封装，不处理业务状态流转。
 */

// 债权发布

// 保存草稿：无 id 新建，有 id 修改；payload 为债权发布向导表单数据
export const saveDraft = async (payload) => {
  return api.post('/api/pubs/v1/debt/draft', payload);
};

// 提交审核：后端校验通过后置为 PENDING；payload 为草稿提交数据
export const submitDebt = async (payload) => {
  return api.post('/api/pubs/v1/debt/submit', payload);
};

// 撤销：将已发布/待审核记录撤销为草稿
export const revokeDebt = async (id) => {
  return api.post(`/api/pubs/v1/debt/${id}/revoke`);
};

// 我的债权列表：支持分页、筛选与排序；params 常见字段：page/size/keyword/debt_type/audit_status/sort_field/sort_order
export const getMyDebts = async (params) => {
  return api.get('/api/pubs/v1/debt/list/my', { params });
};

// 详情：返回主表与关联数据（债权人、债务人、证据）
export const getDebtDetail = async (id) => {
  return api.get(`/api/pubs/v1/debt/${id}`);
};

// 添加证据：向指定债权追加证据记录；evidence 结构与后端 Evidence DTO 对齐
export const addEvidence = async (id, evidence) => {
  return api.post(`/api/pubs/v1/debt/${id}/evidence`, evidence);
};

// 表单配置：债权类型、处置方式与低价转让选项
export const getFormConfig = async () => {
  return api.get('/api/pubs/v1/debt/form-config');
};

// 删除：仅草稿或撤销状态允许删除
export const deleteDebt = async (id) => {
  return api.post(`/api/pubs/v1/debt/${id}/delete`);
};

// 审核处理：草稿先转 PENDING，随后发布（后台流程）
export const auditProcess = async (id) => {
  return api.post(`/api/pubs/v1/debt/${id}/audit/process`);
};

// 催告历史：按创建时间倒序返回摘要
export const getNoticeHistory = async (id) => {
  return api.get(`/api/pubs/v1/debt/notice/${id}/history`);
};

// 恢复草稿：仅撤销状态允许恢复
export const restoreDebt = async (id) => {
  return api.post(`/api/pubs/v1/debt/${id}/restore`);
};

// 催告模板

// 催告模板：列表查询（分页/筛选/排序）
export const getNoticeTemplates = async (params) => {
  return api.get('/api/pubs/v1/debt/notice/templates', { params });
};

// 文件上传（统一走文件服务）：返回签名 URL 与记录 ID；businessType/businessId 用于后端关联业务数据
export const uploadBusinessFile = async (file, businessType, businessId) => {
  const form = new FormData();
  form.append('file', file);
  form.append('businessType', businessType);
  form.append('businessId', businessId);
  return api.post('/api/pubs/v1/file/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const saveNoticeTemplate = async (payload) => {
  return api.post('/api/pubs/v1/debt/notice/template/save', payload);
};

export const deleteNoticeTemplate = async (id) => {
  return api.delete(`/api/pubs/v1/debt/notice/template/${id}`);
};

// 模板内容：format 常见为 html/json（由后端约定）
export const getNoticeTemplateContent = async (id, format) => {
  return api.get(`/api/pubs/v1/debt/notice/template/${id}/content`, { params: { format } });
};

// 生成正文 PDF：payload 为编辑内容/变量等数据，由后端生成并落库附件记录
export const generateNoticeBodyPdfs = async (debtId, payload) => {
  return api.post(`/api/pubs/v1/debt/notice/${debtId}/generate-pdf`, payload);
};

// 预览正文 PDF：返回 blob，用于前端直接预览
export const previewNoticeBodyPdf = async (debtId, payload) => {
  return api.post(`/api/pubs/v1/debt/notice/${debtId}/preview-body-pdf`, payload, { responseType: 'blob' });
};

// 发送校验码：用于发送前的校验/确认（按后端策略）
export const sendDebtNoticeCode = async (debtId) => {
  return api.post(`/api/pubs/v1/debt/notice/${debtId}/send-code`);
};

// 发送重发催告校验码：用于重发前的校验/确认（按后端策略）
export const sendDebtNoticeResendCode = async (batchId) => {
  return api.post(`/api/pubs/v1/debt/notice/batch/${batchId}/send-resend-code`);
};

// 发送催告：payload 包含发送渠道/接收人等信息（按后端约定）
export const sendDebtNotice = async (debtId, payload) => {
  return api.post(`/api/pubs/v1/debt/notice/${debtId}/send`, payload);
};

// 催告历史批次：分页列表
export const listDebtNoticeBatches = async (params) => {
  return api.get('/api/pubs/v1/debt/notice/history/batches', { params });
};

// 催告批次重发：payload 为重发配置（按后端约定）
export const resendDebtNoticeBatch = async (batchId, payload) => {
  return api.post(`/api/pubs/v1/debt/notice/batch/${batchId}/resend`, payload);
};

// 查询催告批次状态：用于页面轮询/展示统计
export const getDebtNoticeBatchStatus = async (batchId) => {
  return api.get(`/api/pubs/v1/debt/notice/batch/${batchId}/status`);
};

// 催告文件：登录态下载URL（用于债权人后台自查，不触发“已被访问”）
export const getDebtNoticeInternalDownloadUrl = async (token) => {
  return api.get(`/api/pubs/v1/debt/notice/internal/download-url/${token}`);
};

// 文件删除：支持对象键或签名 URL
export const deleteBusinessFile = async (fileUrl, businessType, businessId) => {
  return api.delete('/api/pubs/v1/file/delete', {
    params: { fileUrl, businessType, businessId },
  });
};

// 债权广场

// 广场列表：分页查询已发布债权
export const getSquareDebts = async (params) => {
  return api.get('/api/pubs/v1/debt/square/list', { params });
};

// 收藏/意向

// 添加收藏：记录收藏关系
export const addFavorite = async (debtId) => {
  return api.post(`/api/pubs/v1/debt/${debtId}/favorite`);
};

// 取消收藏：移除收藏关系
export const removeFavorite = async (debtId) => {
  return api.delete(`/api/pubs/v1/debt/${debtId}/favorite`);
};

// 收藏夹列表：支持按收藏时间排序
export const getFavorites = async (params) => {
  return api.get('/api/pubs/v1/debt/favorites', { params });
};

// 提交意向：payload 常见字段 intent_type、name、contact_phone、message
export const submitIntent = async (debtId, payload) => {
  return api.post(`/api/pubs/v1/debt/${debtId}/intent`, payload);
};

// 我收到的投资/代理催收意向：用于“我的债权-投资代理”页面
export const listReceivedDebtIntents = async (params) => {
  return api.get('/api/pubs/v1/debt/intent/list/received', { params });
};

// 跟进日志：列表与新增
export const getFollowupLogs = async (debtId, params) => {
  return api.get(`/api/pubs/v1/debt/followup/${debtId}/logs`, { params });
};
export const createFollowupLog = async (debtId, payload) => {
  return api.post(`/api/pubs/v1/debt/followup/${debtId}/logs`, payload);
};

// 催收状态：暂停/恢复/结案/重新开启
export const pauseCollection = async (debtId) => {
  return api.post(`/api/pubs/v1/debt/followup/${debtId}/collection/pause`);
};
export const resumeCollection = async (debtId) => {
  return api.post(`/api/pubs/v1/debt/followup/${debtId}/collection/resume`);
};
export const closeCollection = async (debtId) => {
  return api.post(`/api/pubs/v1/debt/followup/${debtId}/collection/close`);
};
export const reopenCollection = async (debtId) => {
  return api.post(`/api/pubs/v1/debt/followup/${debtId}/collection/reopen`);
};

// 复制为草稿：仅允许从“已发布”债权复制；返回新草稿ID
export const duplicateDebt = async (id) => {
  return api.post(`/api/pubs/v1/debt/${id}/duplicate`);
};

// 批量发布

// 批量发布：创建上传工作区
export const createBatchWorkspace = async (payload) => {
  return api.post('/api/pubs/v1/debt/batch/workspace', payload);
};

// 批量发布：预检（传入批次ID与行项目）
export const batchPrecheck = async (payload) => {
  return api.post('/api/pubs/v1/debt/batch/precheck', payload);
};

// 批量发布：导入（传入批次ID与行项目）
export const batchImport = async (payload) => {
  return api.post('/api/pubs/v1/debt/batch/import', payload);
};

// 批量发布：我的批次列表
export const getMyBatchList = async (params) => {
  return api.get('/api/pubs/v1/debt/batch/list/my', { params });
};

// 批量发布：批次报表
export const getBatchReport = async (batchId) => {
  return api.get(`/api/pubs/v1/debt/batch/report/${batchId}`);
};

// 批量发布：获取批量处理结果报表
export const downloadBatchErrorReport = async (batchId) => {
  return api.get(`/api/pubs/v1/debt/batch/report/${batchId}/errors.xlsx`, { responseType: 'blob' });
};

// 批量发布：上传文件创建工作区（multipart/form-data）
export const createBatchWorkspaceByUpload = async (file, mode) => {
  const form = new FormData();
  form.append('file', file);
  form.append('mode', mode || 'full');
  return api.post('/api/pubs/v1/debt/batch/workspace/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// 对外分享（share-link）

// 创建债权分享：返回 token（用于拼接公开访问链接）
export const createDebtShare = async (debtId) => {
  return api.post(`/api/pubs/v1/share/debt/${debtId}`);
};

// 获取债权公开分享数据：token 来自分享链接
export const getPublicDebtShare = async (token) => {
  return api.get(`/api/pubs/v1/share/public/debt/${token}`);
};

// 创建催告批次分享：返回 token（用于外部查看批次与下载入口）
export const createNoticeBatchShare = async (batchId) => {
  return api.post(`/api/pubs/v1/share/notice-batch/${batchId}`);
};

// 获取催告批次公开分享数据：token 来自分享链接
export const getPublicNoticeBatchShare = async (token) => {
  return api.get(`/api/pubs/v1/share/public/notice-batch/${token}`);
};

// 创建单封催告函分享：返回 token（用于外部查看单个收件人催告信息）
export const createNoticeLetterShare = async (recipientId) => {
  return api.post(`/api/pubs/v1/share/notice-letter/${recipientId}`);
};

// 获取单封催告函公开分享数据：token 来自分享链接
export const getPublicNoticeLetterShare = async (token) => {
  return api.get(`/api/pubs/v1/share/public/notice-letter/${token}`);
};

// 通过函件代码换取催告函分享 token（用于跳转到公示页）
export const getPublicNoticeLetterTokenByCode = async (code) => {
  return api.get(`/api/pubs/v1/share/public/notice-letter/by-code/${code}`);
};
